<template>
  <div ref="dropdownRef" class="relative w-full">
    <BaseInput
      v-model="query"
      @focus="openDropdown"
      @click="openDropdown"
      aria-label="Search"
      :placeholder="placeholder || 'Search'"
      class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-1 focus:ring-gray-900 focus:outline-none"
      id="ingredient-input"
      @keydown.escape.prevent="closeAndFoucs"
    />
    <ul
      v-if="isOpen && filteredOptions.length"
      class="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-auto 
      max-h-[14.3rem] pt-[0.5rem] pb-[0.5rem]"
    >
      <li
        v-for="(option,i) in filteredOptions"
        :key="option"
        ref="optionRefs"
        @mousedown.prevent="addTag(option)"
        @keydown.down.prevent="() => moveFocus(i + 1)"
        @keydown.up.prevent="() => moveFocus(i - 1)"
        @keydown.enter.prevent="() => addTag(option)"
        @keydown.tab.prevent="closeAndFoucs"
        @keydown.escape.prevent="closeAndFoucs"
        tabindex="0"
        class="pl-[1rem] pr-[3rem] py-[0.375rem] cursor-pointer hover:bg-gray-100 truncate"
      >
        {{ option }}
      </li>
    </ul>
    <div class="mt-[1.0714285714rem] flex flex-wrap gap-2">
      <Tag
        v-for="tag in selectedTags"
        :key="tag"
        :label="tag"
        @remove="removeTag(tag)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import BaseInput from '../../atom/baseInput.vue'
import Tag from '../../atom/tag.vue'

const props = withDefaults(defineProps<{
  modelValue: string[]
  options: string[]
  placeholder?: string
}>(), {
  modelValue: () => [],
  options: () => [],
  placeholder: 'Search'
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
  (e: 'dropdown-closed'): void
}>()

const query = ref('')
const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const selectedTags = computed<string[]>({
  get: () => props.modelValue || [],
  set: (val: string[]) => emit('update:modelValue', val)
})

const filteredOptions = computed(() => {
  const queryValue = query.value.trim().toLowerCase()
  const chosen = new Set(selectedTags.value.map(value => value.toLowerCase()))
  return props.options
    .filter(opt => opt && !chosen.has(opt.toLowerCase()))
    .filter(opt => (queryValue.length <= 1 ? true : opt.toLowerCase().includes(queryValue)))
})

const addTag = (tag: string) => {
  const next = Array.from(new Set([...selectedTags.value, tag]))
  selectedTags.value = next
  query.value = ''
  isOpen.value = false
}

const removeTag = (tag: string) => {
  selectedTags.value = selectedTags.value.filter(tagValue => tagValue !== tag)
}

const openDropdown = () => {
  if (filteredOptions.value.length) isOpen.value = true
}

watch(query, (newQuery) => {
  isOpen.value = newQuery.trim().length > 1 && filteredOptions.value.length > 0
})

const closeDropdown = () => { isOpen.value = false }

const closeAndFoucs = () => { 
  isOpen.value = false;
  emit('dropdown-closed');
}

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

const optionRefs = ref<HTMLElement[]>([])

const moveFocus = (index: number) => {
  if (!optionRefs.value.length) return
  if (index < 0) index = optionRefs.value.length - 1
  if (index >= optionRefs.value.length) index = 0
  optionRefs.value[index]?.focus()
}

</script>
